import { COLLECTORS } from './collectors';
import { detectProtections, protectionsSignal, uniformModeLikely, type Protection } from './collectors/privacy-signals';
import type { CategoryResult, Collector, CollectorStatus, Signal } from './collectors/types';
import { prop, TimeoutError, withTimeout } from './collectors/util';
import { ESTIMATES } from './entropy/estimates';
import { canonicalJson } from './entropy/hash';
import { adjustForContext, applyVolatility, buildStableInput, computeScore, stableHash, type ScoreResult } from './entropy/score';

export const COLLECTOR_TIMEOUT_MS = 3000;

export type ProgressStatus = 'running' | CollectorStatus;
export type Phase = 'sampling' | 'resampling' | 'scoring';

export interface RunOptions {
  collectors?: readonly Collector[];
  timeoutMs?: number;
  onProgress?: (id: string, status: ProgressStatus, ms?: number) => void;
  onPhase?: (phase: Phase) => void;
}

export interface RunResult {
  startedAt: string;
  durationMs: number;
  categories: CategoryResult[];
  signals: Signal[];
  /** Stable signals whose value differed between the two sampling runs. */
  changedBetweenRuns: string[];
  protections: Protection[];
  uniformMode: boolean;
  score: ScoreResult;
  /** `hex` is empty (and `error` set) if Web Crypto is unavailable, e.g. on plain-HTTP origins. */
  stable: { input: Record<string, unknown>; hex: string; error?: string };
}

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * Run every collector concurrently. Each gets its own timeout, and a rejection or timeout in one
 * never affects the others.
 */
export async function runCollectors(
  collectors: readonly Collector[],
  timeoutMs = COLLECTOR_TIMEOUT_MS,
  onProgress?: RunOptions['onProgress'],
): Promise<CategoryResult[]> {
  const settled = await Promise.allSettled(
    collectors.map(async (c): Promise<CategoryResult> => {
      const base = { id: c.id, title: c.title, description: c.description };
      const t0 = now();
      onProgress?.(c.id, 'running');
      let result: CategoryResult;
      try {
        const signals = await withTimeout(Promise.resolve().then(() => c.collect()), timeoutMs, c.title);
        result = { ...base, status: 'ok', signals: Array.isArray(signals) ? signals : [], ms: Math.round(now() - t0) };
      } catch (err) {
        result = {
          ...base,
          status: err instanceof TimeoutError ? 'timeout' : 'error',
          signals: [],
          ms: Math.round(now() - t0),
          error: err instanceof Error ? err.message : String(err),
        };
      }
      onProgress?.(c.id, result.status, result.ms);
      return result;
    }),
  );
  return settled.map((s, i) => {
    if (s.status === 'fulfilled') return s.value;
    const c = collectors[i]!;
    return { id: c.id, title: c.title, description: c.description, status: 'error', signals: [], ms: 0, error: String(s.reason) };
  });
}

/** Ids of stable, available signals whose value differs between two runs. */
export function diffRuns(first: readonly CategoryResult[], second: readonly CategoryResult[]): string[] {
  const values = new Map<string, string>();
  for (const s of second.flatMap((c) => c.signals)) if (s.available) values.set(s.id, canonicalJson(s.value));
  const changed: string[] = [];
  for (const s of first.flatMap((c) => c.signals)) {
    if (!s.available || !ESTIMATES[s.id]?.stable) continue;
    const other = values.get(s.id);
    if (other !== undefined && other !== canonicalJson(s.value)) changed.push(s.id);
  }
  return changed.sort();
}

export async function runCheck(opts: RunOptions = {}): Promise<RunResult> {
  const collectors = opts.collectors ?? COLLECTORS;
  const timeoutMs = opts.timeoutMs ?? COLLECTOR_TIMEOUT_MS;
  const startedAt = new Date().toISOString();
  const t0 = now();

  opts.onPhase?.('sampling');
  const first = await runCollectors(collectors, timeoutMs, opts.onProgress);
  // A second, silent pass: anything stable that changes is being randomized.
  opts.onPhase?.('resampling');
  const second = await runCollectors(collectors, timeoutMs);
  opts.onPhase?.('scoring');

  const changed = diffRuns(first, second);
  let signals = applyVolatility(
    first.flatMap((c) => c.signals),
    new Set(changed),
  );
  const byId = new Map(signals.map((s) => [s.id, s]));
  const protections = detectProtections(byId, changed);
  const uniformMode = uniformModeLikely(byId);
  const brave = prop<boolean>(byId.get('nav.vendorGlobals')?.value, 'brave') === true;
  signals = adjustForContext(signals, { uniformMode, brave });

  const summary = protectionsSignal(protections);
  const finalById = new Map(signals.map((s) => [s.id, s]));
  const categories = first.map((c) => {
    const updated = c.signals.map((s) => finalById.get(s.id) ?? s);
    return { ...c, signals: c.id === 'privacy' ? [...updated, summary] : updated };
  });
  signals = categories.flatMap((c) => c.signals);

  return {
    startedAt,
    durationMs: Math.round(now() - t0),
    categories,
    signals,
    changedBetweenRuns: changed,
    protections,
    uniformMode,
    score: computeScore(signals),
    stable: await stableHash(signals).catch((err: unknown) => ({
      input: buildStableInput(signals),
      hex: '',
      error: err instanceof Error ? err.message : String(err),
    })),
  };
}
